import React from "react";
import { motion } from "framer-motion";
import { classes } from "./tokens";
import { useCarServicePackage } from "./CarServicePackageContext";
import { BODY_CLASS_FROM_API } from "../../services/publicCrmApi";

/**
 * Pricing Tiers — transparency table. Live from CRM /service-packages
 * (pricing_matrix), with a static fallback so the LP works offline / on
 * cold-start before the API responds.
 */
const FALLBACK_TIERS = [
  { label: "Hatchback",   examples: "Swift · Baleno · i20 · Tiago · Wagon R",      petrol: 2499, diesel: 2999, cng: 2799 },
  { label: "Sedan",       examples: "Dzire · Amaze · Aura · Verna",                petrol: 2999, diesel: 3499, cng: null },
  { label: "Compact SUV", examples: "Nexon · Brezza · Sonet · Magnite · Venue",    petrol: 3499, diesel: 3999, cng: null },
  { label: "Mid SUV",     examples: "Creta · Seltos · Hyryder · Kushaq",           petrol: 3999, diesel: 4499, cng: null },
  { label: "Premium",     examples: "Innova · Fortuner · Hector · XUV700",         petrol: 4499, diesel: 4999, cng: null },
];

const EXAMPLE_BY_BODY_CLASS = Object.fromEntries(
  FALLBACK_TIERS.map((t) => [t.label, t.examples]),
);

const fmt = (n) => (n == null ? "—" : `₹${n.toLocaleString("en-IN")}`);

/**
 * Maps the API pricing_matrix payload to the row shape the table expects.
 * Order is preserved by walking FALLBACK_TIERS so display stays stable
 * even if the API returns body_classes in a different order.
 */
function buildLiveTiers(pkg) {
  const matrix = pkg?.pricing_matrix;
  if (!matrix) return null;
  return FALLBACK_TIERS.map((row) => {
    const cell = matrix[row.label] || {};
    return {
      label: row.label,
      examples: EXAMPLE_BY_BODY_CLASS[row.label] || "",
      petrol: cell.Petrol ?? null,
      diesel: cell.Diesel ?? null,
      cng: cell.CNG ?? null,
    };
  });
}

const PricingTiers = () => {
  const { pkg } = useCarServicePackage();
  const tiers = buildLiveTiers(pkg) || FALLBACK_TIERS;
  // Suppress lint warning while we keep the helper exported for future use.
  void BODY_CLASS_FROM_API;

  return (
    <section
      className="py-20 lg:py-28 bg-slate-50 border-t border-slate-200"
      data-testid="pricing-tiers-section"
    >
      <div className={classes.container}>
        <div className="max-w-2xl mb-12">
          <span className={classes.overline}>Transparent Pricing</span>
          <h2 className={`${classes.h2} mt-3`}>
            Same job, same price.<br />
            <span className="text-slate-500">Across all 500+ cities.</span>
          </h2>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-sm">
          {/* Header row */}
          <div className="hidden sm:grid grid-cols-12 px-6 py-4 bg-slate-900 text-white text-[11px] font-bold uppercase tracking-[0.18em]">
            <div className="col-span-5">Body Class</div>
            <div className="col-span-2 text-right">Petrol</div>
            <div className="col-span-2 text-right">Diesel</div>
            <div className="col-span-2 text-right">CNG</div>
            <div className="col-span-1" />
          </div>

          {tiers.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.3, delay: i * 0.04 }}
              className="grid grid-cols-12 px-6 py-5 border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors gap-y-1"
              data-testid={`pricing-row-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="col-span-12 sm:col-span-5">
                <div className="text-base font-bold text-slate-900">{t.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.examples}</div>
              </div>
              <div className="col-span-4 sm:col-span-2 sm:text-right">
                <div className="sm:hidden text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Petrol</div>
                <div className="font-mono font-bold text-slate-900">{fmt(t.petrol)}</div>
              </div>
              <div className="col-span-4 sm:col-span-2 sm:text-right">
                <div className="sm:hidden text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Diesel</div>
                <div className="font-mono font-bold text-slate-900">{fmt(t.diesel)}</div>
              </div>
              <div className="col-span-4 sm:col-span-2 sm:text-right">
                <div className="sm:hidden text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">CNG</div>
                <div className="font-mono font-bold text-slate-900">{fmt(t.cng)}</div>
              </div>
              <div className="hidden sm:block col-span-1" />
            </motion.div>
          ))}
        </div>

        <p className="mt-4 text-xs text-slate-500">
          * All prices include OEM oil + filter, 25-point inspection, doorstep service, and 1,000 km warranty. GST included.
        </p>
      </div>
    </section>
  );
};

export default PricingTiers;
